/**
 * ExamTypeChips Component
 * Display exam types as colored chips (max 2 visible + overflow tooltip)
 */
import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Loop as LoopIcon,
  PersonOff as PersonOffIcon,
  AddCircle as AddCircleIcon,
  Work as WorkIcon,
  SwapHoriz as SwapHorizIcon,
  LocalHospital as LocalHospitalIcon,
} from '@mui/icons-material';
import { OccupationalExam } from '../../../types/occupationalExam';

const MAX_VISIBLE = 2;

interface ExamTypeChipsProps {
  exam: OccupationalExam;
}

const examTypeConfig: Array<{
  key: keyof OccupationalExam;
  label: string;
  fullLabel: string;
  color: 'success' | 'primary' | 'warning' | 'info' | 'secondary' | 'default';
  icon: React.ReactElement;
}> = [
  { key: 'preEmployment', label: 'Admis.', fullLabel: 'Admissional', color: 'success', icon: <PersonAddIcon /> },
  { key: 'periodic', label: 'Period.', fullLabel: 'Periódico', color: 'primary', icon: <LoopIcon /> },
  { key: 'dismissal', label: 'Demis.', fullLabel: 'Demissional', color: 'warning', icon: <PersonOffIcon /> },
  { key: 'complementary', label: 'Compl.', fullLabel: 'Complementar', color: 'info', icon: <AddCircleIcon /> },
  { key: 'returnToWork', label: 'Retorno', fullLabel: 'Retorno ao Trabalho', color: 'secondary', icon: <WorkIcon /> },
  { key: 'riskChange', label: 'Mud. Risco', fullLabel: 'Mudança de Risco', color: 'default', icon: <SwapHorizIcon /> },
  { key: 'clinicalConsultation', label: 'Consulta', fullLabel: 'Consulta Clínica', color: 'info', icon: <LocalHospitalIcon /> },
];

export const ExamTypeChips: React.FC<ExamTypeChipsProps> = ({ exam }) => {
  const activeTypes = examTypeConfig.filter((type) => exam[type.key] === true);

  if (activeTypes.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Nenhum tipo
      </Typography>
    );
  }

  const visible = activeTypes.slice(0, MAX_VISIBLE);
  const overflow = activeTypes.slice(MAX_VISIBLE);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center' }}>
      {visible.map((type) => (
        <Tooltip key={type.key} title={type.fullLabel}>
          <Chip label={type.label} size="small" color={type.color} variant="outlined" />
        </Tooltip>
      ))}
      {overflow.length > 0 && (
        <Tooltip
          title={
            <Box>
              {activeTypes.map((t) => (
                <Typography key={t.key} variant="body2">{t.fullLabel}</Typography>
              ))}
            </Box>
          }
        >
          <Chip label={`+${overflow.length}`} size="small" variant="outlined" />
        </Tooltip>
      )}
    </Box>
  );
};

export default ExamTypeChips;
